import { useState } from 'react';
import { Link } from 'react-router';
import {
  COUNCIL_DATASET_CSV_PATH,
  COUNCIL_DATASET_JSON_PATH,
  COUNCIL_DATASET_METADATA,
  OPEN_GOVERNMENT_LICENCE_V3,
} from '@fixtyer/content';
import { SEO, BreadcrumbSchema, FAQSchema, DatasetSchema } from '@/components/seo/seo';
import { clsx } from 'clsx';
import { councilPages, getCouncilDisplayName } from '@/lib/council-pages';
import {
  STATIC_AUTHORITY_LEAGUE_TABLE,
  type AuthorityLeagueTable,
  type AuthorityRating,
} from '@/data/authority-league-table';

function RatingPill({ rating }: { rating: 'green' | 'amber' | 'red' | null }) {
  if (!rating) return <span className="text-surface-400">—</span>;
  
  const styles = {
    green: 'bg-emerald-500 text-white',
    amber: 'bg-amber-500 text-white',
    red: 'bg-rose-500 text-white',
  };
  
  return (
    <span className={clsx('inline-block w-3 h-3 rounded-full', styles[rating])} 
      title={rating.charAt(0).toUpperCase() + rating.slice(1)} 
    />
  );
}

function AuthorityTable({ 
  authorities, 
  rating, 
  title, 
  description,
  accentColor 
}: { 
  authorities: AuthorityRating[]; 
  rating: 'red' | 'amber' | 'green';
  title: string;
  description: string;
  accentColor: string;
}) {
  const filtered = authorities.filter(a => a.dftOverallRating === rating);
  if (filtered.length === 0) return null;

  const captionText = rating === 'red' 
    ? `${filtered.length} English highway authorities with a RED DfT network-level rating`
    : rating === 'amber'
    ? `${filtered.length} English highway authorities with an AMBER DfT network-level rating`
    : `${filtered.length} English highway authorities with a GREEN DfT network-level rating`;

  return (
    <div className="mb-12" id={`${rating}-list`}>
      <div className={clsx('border-l-4 pl-6 mb-6', accentColor)}>
        <div className="flex items-center gap-3 mb-2">
          <h3 className="font-display text-2xl font-bold text-brand-950">{title}</h3>
          <span className={clsx(
            'px-3 py-1 rounded-full text-sm font-bold',
            rating === 'red' ? 'bg-rose-100 text-rose-800' :
            rating === 'amber' ? 'bg-amber-100 text-amber-800' :
            'bg-emerald-100 text-emerald-800'
          )}>
            {filtered.length} {filtered.length === 1 ? 'council' : 'councils'}
          </span>
        </div>
        <p className="text-surface-600 max-w-2xl">{description}</p>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden shadow-sm">
        <table className="w-full">
          <caption className="sr-only">{captionText}</caption>
          <thead>
            <tr className="bg-surface-50 border-b border-surface-200 text-left">
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide">Council</th>
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide hidden sm:table-cell">Region</th>
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide text-center">Overall</th>
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide text-center hidden md:table-cell">Condition</th>
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide text-center hidden md:table-cell">Spend</th>
              <th className="px-4 py-3 text-xs font-semibold text-surface-600 uppercase tracking-wide text-center hidden lg:table-cell">Best Practice</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-100">
            {filtered.sort((a, b) => a.name.localeCompare(b.name)).map((auth) => {
              const hasPage = councilPages[auth.name];
              const displayName = getCouncilDisplayName(auth.name);
              return (
                <tr key={auth.id} className="hover:bg-surface-50 transition-colors">
                  <td className="px-4 py-3">
                    {hasPage ? (
                      <Link to={hasPage} className="font-medium text-brand-600 hover:text-brand-800 hover:underline">
                        {displayName}
                        <svg className="inline-block w-3 h-3 ml-1 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    ) : (
                      <span className="font-medium text-brand-950">{displayName}</span>
                    )}
                    <span className="sm:hidden text-xs text-surface-500 block">{auth.region}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-surface-600 hidden sm:table-cell">{auth.region}</td>
                  <td className="px-4 py-3 text-center"><RatingPill rating={auth.dftOverallRating} /></td>
                  <td className="px-4 py-3 text-center hidden md:table-cell"><RatingPill rating={auth.dftConditionRating} /></td>
                  <td className="px-4 py-3 text-center hidden md:table-cell"><RatingPill rating={auth.dftSpendRating} /></td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell"><RatingPill rating={auth.dftBestPracticeRating} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RoadMaintenanceReportPage() {
  const [data] = useState<AuthorityLeagueTable>(STATIC_AUTHORITY_LEAGUE_TABLE);

  return (
    <div className="min-h-screen">
      <SEO
        title="DfT Road Ratings 2025-26: All English Authorities | Fixtyer"
        description="Compare all 154 English highway authorities in the DfT 2025-2026 road maintenance ratings: 13 RED, 125 AMBER and 16 GREEN. Context, not proof of liability."
        canonical="/road-maintenance-report"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Road Maintenance Report', url: '/road-maintenance-report' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'What do the DfT road maintenance ratings mean?',
            answer: 'The Department for Transport rates local highway authorities as Red, Amber, or Green using published road condition, spending and best-practice criteria. These are network-level assessments.',
          },
          {
            question: 'How do these ratings affect my pothole claim?',
            answer: 'A rating may provide background context, but it is not proof of liability and does not automatically defeat a Section 58 defence. The facts and records for the specific defect matter.',
          },
          {
            question: 'Where does this data come from?',
            answer: 'All data comes from the official Department for Transport Local Road Maintenance Ratings 2025-2026, published on GOV.UK and based on council transparency reports.',
          },
          {
            question: 'Why are some councils rated Red while others are Green?',
            answer: 'Ratings reflect published road condition, spending and best-practice criteria. They are comparative indicators and do not decide individual pothole claims.',
          },
        ]}
      />
      <DatasetSchema
        name="England Local Road Maintenance Ratings 2025-2026"
        description="The published Department for Transport overall, road condition, spend and best-practice rating bands for 154 English highway authorities, including Transport for London."
        url="/road-maintenance-report"
        datePublished={COUNCIL_DATASET_METADATA.publishedDate}
        dateModified={COUNCIL_DATASET_METADATA.modifiedDate}
        license={OPEN_GOVERNMENT_LICENCE_V3}
        temporalCoverage={COUNCIL_DATASET_METADATA.temporalCoverage}
        spatialCoverage={COUNCIL_DATASET_METADATA.spatialCoverage}
        distribution={[
          {
            name: 'Council road maintenance ratings 2025-2026 v1 CSV',
            contentUrl: COUNCIL_DATASET_CSV_PATH,
            encodingFormat: 'text/csv',
          },
          {
            name: 'Council road maintenance ratings 2025-2026 v1 JSON',
            contentUrl: COUNCIL_DATASET_JSON_PATH,
            encodingFormat: 'application/json',
          },
        ]}
        keywords={['UK roads', 'pothole claims', 'council ratings', 'DfT', 'road maintenance', 'Section 58', 'highways act']}
        variableMeasured={['Road Condition', 'Maintenance Spend', 'Best Practice Adoption', 'Inspection Frequency']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top diagonal stripe bar - chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Diagonal stripes accent */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <pattern id="diag-stripes" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="10" stroke="white" strokeWidth="4" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#diag-stripes)" />
          </svg>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-28 lg:pb-24">
          <div className="flex items-center gap-2 text-brand-200 text-sm mb-4">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>England-only DfT data • 2025-2026</span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            DfT Road Maintenance Ratings 2025-2026
            <span className="block text-amber-400 mt-2">All 154 English Highway Authorities</span>
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed">
            The DfT rated 154 English highway authorities for 2025-2026: <strong className="text-white">13 RED,
            125 AMBER and 16 GREEN</strong>. These are network-level bands and do not establish notice or liability
            for a specific pothole.
          </p>
          <p className="mb-6 text-sm font-medium text-brand-200">Last reviewed: 19 July 2026</p>

          <div className="flex flex-wrap gap-4 text-sm">
            <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Official DfT Data
            </a>
            <span className="px-4 py-2 bg-white/5 rounded-lg">
              📅 Rating Period: 2025-2026
            </span>
            <Link className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" to="/pothole-hall-of-shame">
              See the 13 RED-rated authorities
            </Link>
            <Link className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" to="/data/council-road-maintenance-ratings">
              Dataset and methodology
            </Link>
          </div>
        </div>

        {/* Bottom diagonal stripe bar - chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Key Stats */}
      {data.stats && (
        <section className="bg-white border-b border-surface-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-2xl bg-surface-50">
                <div className="text-4xl font-bold text-brand-900">{data.stats.total}</div>
                <div className="text-sm text-surface-600 mt-1">Councils Rated</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-rose-50">
                <div className="text-4xl font-bold text-rose-600">{data.stats.red}</div>
                <div className="text-sm text-rose-700 mt-1">Red rated</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-amber-50">
                <div className="text-4xl font-bold text-amber-600">{data.stats.amber}</div>
                <div className="text-sm text-amber-700 mt-1">Amber rated</div>
              </div>
              <div className="text-center p-6 rounded-2xl bg-emerald-50">
                <div className="text-4xl font-bold text-emerald-600">{data.stats.green}</div>
                <div className="text-sm text-emerald-700 mt-1">Green rated</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why This Matters */}
      <section className="bg-gradient-to-br from-accent-50 to-brand-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
                Why This Matters For Your Claim
              </h2>
              <div className="space-y-4 text-surface-700">
                <p>
                  When councils reject pothole damage claims, their most common defence is: 
                  <em>"We had no prior knowledge of this defect."</em>
                </p>
                <p>
                  Ratings provide context, not proof of liability, and do not automatically defeat a Section 58 defence.
                  A <strong className="text-rose-600">RED</strong> or <strong className="text-amber-600">AMBER</strong> rating
                  is a network-level assessment; it does not establish notice of the pothole or whether a
                  reasonable inspection and repair system operated at the incident location.
                </p>
                <p>
                  <strong>Mac can include the source rating in your claim pack as background context</strong> for
                  you to review alongside defect-specific evidence.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/check"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg"
                >
                  Start Your Claim
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-surface-200">
              <h3 className="font-display text-lg font-semibold text-brand-950 mb-4">
                📊 How Ratings Are Calculated
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-brand-700">1</span>
                  <div>
                    <strong className="text-brand-950">Road Condition</strong>
                    <p className="text-sm text-surface-600">Current state of A, B/C and unclassified roads</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-brand-700">2</span>
                  <div>
                    <strong className="text-brand-950">Spend Score</strong>
                    <p className="text-sm text-surface-600">How much councils invest in maintenance vs their allocation</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold text-brand-700">3</span>
                  <div>
                    <strong className="text-brand-950">Best Practice</strong>
                    <p className="text-sm text-surface-600">Adoption of innovation, decarbonisation, and footway maintenance</p>
                  </div>
                </li>
              </ul>
              <p className="mt-6 text-xs text-surface-500">
                Source: <a 
                  href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline"
                >
                  Department for Transport
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
              The Full Breakdown
            </h2>
            <p className="text-surface-600 max-w-2xl mx-auto">
              Every local highway authority in England, grouped by their overall DfT rating. 
              Find your council and see how they're performing.
            </p>
          </div>

          <>
              {/* RED-rated councils */}
              <AuthorityTable
                authorities={data.authorities}
                rating="red"
                title="Red-rated councils"
                description="These authorities received the lower DfT network-level rating. The rating alone does not establish a breach of duty or liability for a specific incident."
                accentColor="border-rose-500"
              />

              {/* AMBER - Room for Improvement */}
              <AuthorityTable
                authorities={data.authorities}
                rating="amber"
                title="⚠️ Room for Improvement"
                description="The majority of councils fall into this middle network-level category. Check defect-specific inspection, report and repair records when assessing an individual incident."
                accentColor="border-amber-500"
              />

              {/* GREEN - The Good Performers */}
              <AuthorityTable
                authorities={data.authorities}
                rating="green"
                title="Higher DfT rating band"
                description="These authorities received the higher DfT network-level rating. It does not establish how a specific defect was inspected, reported or repaired."
                accentColor="border-emerald-500"
              />
          </>

          {/* Legend */}
          <div className="mt-12 p-6 bg-white rounded-xl border border-surface-200">
            <h4 className="font-semibold text-brand-950 mb-4">Understanding the Scorecards</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-brand-900">Overall Rating</span>
                <p className="text-surface-600">Combined score across all metrics</p>
              </div>
              <div>
                <span className="font-medium text-brand-900">Condition</span>
                <p className="text-surface-600">Current state of local roads</p>
              </div>
              <div>
                <span className="font-medium text-brand-900">Spend</span>
                <p className="text-surface-600">Investment in maintenance</p>
              </div>
              <div>
                <span className="font-medium text-brand-900">Best Practice</span>
                <p className="text-surface-600">Innovation & efficiency</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What This Means for £7.3bn Funding */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-950 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4">
                £7.3 Billion at Stake
              </h2>
              <p className="text-brand-200 max-w-2xl mb-6">
                To access their full share of the government's £7.3 billion highways maintenance funding 
                (2026/27 to 2029/30), councils must continue publishing transparency reports and demonstrate 
                compliance with best practice. 
              </p>
              <p className="text-brand-200 max-w-2xl mb-8">
                <strong className="text-white">Red-rated councils will receive dedicated government support</strong> to 
                improve their processes. That programme is useful policy context, but does not establish what an
                authority knew about a particular road defect.
              </p>
              <a 
                href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium"
              >
                Read the full DfT guidance
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-brand-50 to-accent-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Hit a pothole? Check your council's rating.
          </h2>
          <p className="text-lg text-surface-700 mb-8">
            Mac can include the source data in your claim pack as background context for you to review.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Start Your Claim
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/authority-ratings"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 font-semibold rounded-xl border-2 border-brand-200 hover:border-brand-300 transition-colors"
            >
              Search All Councils
            </Link>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data source: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>. 
            DfT source published: 11 January 2026. Fixtyer dataset published: 19 July 2026. Last reviewed: 19 July 2026.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
